import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Mail, Waves } from "lucide-react";
import { supabase } from "../supabaseClient";
import { getAuthRedirectUrl } from "../utils/authRedirect";
import { LegalTextLinks } from "../components/LegalDocumentModal";

function getDefaultPath() {
    return Capacitor.isNativePlatform() ? "/home" : "/";
}

function safeNextPath(rawNext) {
    if (!rawNext) return getDefaultPath();
    if (typeof rawNext === "string" && rawNext.startsWith("/")) return rawNext;
    return getDefaultPath();
}

export default function Register() {
    const location = useLocation();
    const isNative = Capacitor.isNativePlatform();
    const params = new URLSearchParams(location.search);
    const next = safeNextPath(location.state?.from || params.get("next"));

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState("");

    const passwordsMatch = password === confirm && confirm.length > 0;
    const passwordReady = password.length >= 6;

    const canSubmit = useMemo(() => {
        const okEmail = email.trim().length > 3;
        return okEmail && passwordReady && passwordsMatch && !busy;
    }, [email, passwordReady, passwordsMatch, busy]);

    const handleRegister = async (e) => {
        e.preventDefault();
        setStatus("");

        if (password !== confirm) {
            setStatus("Passwords do not match.");
            return;
        }

        setBusy(true);
        try {
            const cleanEmail = email.trim().toLowerCase();
            const usernameGuess = cleanEmail.split("@")[0] || null;

            const { error } = await supabase.auth.signUp({
                email: cleanEmail,
                password,
                options: {
                    emailRedirectTo: getAuthRedirectUrl("/auth/callback"),
                    data: {
                        username: usernameGuess,
                        signup_source: "bbp-client",
                        next,
                    },
                },
            });

            if (error) {
                setStatus(error.message);
                return;
            }

            setStatus("Almost there. Check your email to confirm your Beach Bum Pass account.");
        } catch (err) {
            console.error(err);
            setStatus("Registration failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const handleGoogleLogin = async () => {
        setStatus("");
        setBusy(true);

        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                    redirectTo: getAuthRedirectUrl("/auth/callback"),
                },
            });

            if (error) {
                setStatus(error.message);
            }
        } catch (err) {
            console.error(err);
            setStatus("Google signup could not be started.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className={`auth-shell auth-shell--coastal ${isNative ? "auth-shell--native" : "auth-shell--web"}`}>
            <section className="auth-coast-card auth-coast-card--register">
                <header className="auth-logo-header" aria-label="Beach Bum Pass">
                    <Link className="auth-logo-link" to={getDefaultPath()} aria-label="Beach Bum Pass home">
                        <img src="/assets/Graphics/Logo_Transparent.png" alt="" className="auth-logo-mark" />
                        <span className="auth-logo-text">Beach Bum Pass</span>
                    </Link>
                </header>

                <aside className="auth-brand-panel auth-brand-panel--simple" aria-label="Beach Bum Pass signup brand panel">
                    <div className="auth-desktop-logo-lockup" aria-hidden="true">
                        <div className="auth-desktop-logo-orb">
                            <img src="/assets/Graphics/Logo_Transparent.png" alt="" className="auth-brand-logo" />
                        </div>
                        <div className="auth-desktop-brand-copy">
                            <span>Beach Bum Pass</span>
                            <small>Fresh trip</small>
                        </div>
                    </div>

                    <div className="auth-brand-copy">

                        <h1 className="auth-title auth-title--hero">Create Account</h1>
                        <p className="auth-subtitle auth-subtitle--hero">
                            Save your pass, claim crew access, and keep every Beach Bum adventure tied to one simple login.
                        </p>
                    </div>


                </aside>

                <section className="auth-form-panel" aria-labelledby="register-title">
                    <div className="auth-form-heading">
                        <p className="auth-eyebrow">New beach bum</p>
                        <h2 id="register-title" className="auth-title">Create your account.</h2>
                        <p className="auth-subtitle">Use the same email you plan to use for pass purchases and claim codes.</p>
                    </div>

                    <form onSubmit={handleRegister} className="auth-form">
                        {!isNative ? (
                            <>
                                <button className="auth-social" type="button" onClick={handleGoogleLogin} disabled={busy}>
                                    <span className="auth-social-mark">G</span>
                                    Continue with Google
                                </button>
                                <div className="auth-divider" role="presentation">
                                    <span>or start with email</span>
                                </div>
                            </>
                        ) : null}

                        <label className="auth-label">
                            Email
                            <span className="auth-input-wrap">
                                <Mail size={18} aria-hidden="true" />
                                <input
                                    className="auth-input"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    autoComplete="email"
                                    inputMode="email"
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </span>
                        </label>

                        <label className="auth-label">
                            Password
                            <input
                                className="auth-input"
                                type="password"
                                placeholder="Min 6 characters"
                                value={password}
                                autoComplete="new-password"
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </label>

                        <label className="auth-label">
                            Confirm password
                            <input
                                className="auth-input"
                                type="password"
                                placeholder="Re-enter password"
                                value={confirm}
                                autoComplete="new-password"
                                onChange={(e) => setConfirm(e.target.value)}
                                required
                            />
                        </label>

                        <div className="auth-checklist" aria-label="Password checklist">
                            <span className={passwordReady ? "is-ready" : ""}>6+ characters</span>
                            <span className={passwordsMatch ? "is-ready" : ""}>Passwords match</span>
                        </div>

                        <button className="auth-primary" type="submit" disabled={!canSubmit}>
                            {busy ? "Creating�" : "Create account"}
                        </button>

                        <div className="auth-row auth-row--center">
                            <span className="auth-muted">Already have an account?</span>
                            <Link className="auth-link" to={`/login?next=${encodeURIComponent(next)}`}>
                                Log in
                            </Link>
                        </div>

                        {status ? <div className="auth-msg" aria-live="polite">{status}</div> : null}
                    </form>

                    <p className="auth-legal">We will send a confirmation email to verify your address. By creating an account, you agree to Beach Bum Pass <LegalTextLinks />.</p>
                </section>
            </section>
        </main>
    );
}
